/**
 * Utility functions for tenant management
 */

export type TenantId = 'VikenSkog' | 'SBSkog'

/**
 * Interface for the stored value format used by useLocalStorage hook
 */
interface StoredValue {
  value: unknown
  timestamp: number
}

/**
 * Validates if a string is a valid tenant ID
 */
export const isValidTenantId = (id: unknown): id is TenantId => {
  return id === 'VikenSkog' || id === 'SBSkog'
}

/**
 * Maps a string TenantId to the numeric value required by API request bodies.
 * VikenSkog = 1, SBSkog = 2 (matching the backend TenantId enum).
 */
export const toNumericTenantId = (tenantId: TenantId): 1 | 2 => {
  switch (tenantId) {
    case 'VikenSkog':
      return 1
    case 'SBSkog':
      return 2
  }
}

/**
 * Gets the current tenant from localStorage, handling the timestamped storage format
 * used by the useLocalStorage hook.
 *
 * @returns The current tenant ID, defaulting to 'VikenSkog' if none is stored or parsing fails
 */
export const getCurrentTenant = (): TenantId => {
  try {
    const stored = localStorage.getItem('selectedTenant')
    if (!stored) {
      return 'VikenSkog'
    }

    let tenantId: unknown

    try {
      const parsedStored: unknown = JSON.parse(stored)

      // Handle the timestamped format from useLocalStorage hook
      if (typeof parsedStored === 'object' && parsedStored !== null && 'value' in parsedStored) {
        const storedValue = (parsedStored as StoredValue).value
        // If the stored value is a valid tenant ID, use it directly
        if (isValidTenantId(storedValue)) {
          return storedValue
        }
        // If the value is empty/invalid, don't fall back to the object
        tenantId = undefined
      } else {
        // Direct string value (not using timestamped format)
        tenantId = parsedStored
      }
    } catch (parseError) {
      console.warn('Failed to parse stored tenant value from localStorage:', parseError)
      tenantId = undefined
    }

    // Validate the tenant ID using type guard
    if (isValidTenantId(tenantId)) {
      return tenantId
    }

    return 'VikenSkog'
  } catch (error) {
    console.warn('Unexpected error in getCurrentTenant:', error)
    return 'VikenSkog'
  }
}
